import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Star, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { id: null, name: "", message: "", is_default: false };

const SUGGESTED = [
  {
    name: "Please sign and return",
    message: "Please review, sign, and fax back at your earliest convenience. Thank you.",
  },
  {
    name: "Urgent — action required",
    message: "This document requires your urgent attention. Please review and respond as soon as possible.",
  },
  {
    name: "For your records",
    message: "The attached document is for your records. No response is required.",
  },
];

export default function FaxTemplatesSettingsTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fax-cover-templates`);
      setTemplates(res.data || []);
    } catch (err) {
      console.error("Failed to load fax templates", err);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openCreate = (preset = null) => {
    setForm(preset ? { ...EMPTY, ...preset } : EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setForm({
      id: tpl.id,
      name: tpl.name,
      message: tpl.message,
      is_default: !!tpl.is_default,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!form.message.trim()) {
      toast.error("Template message is required");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await axios.patch(`${API}/fax-cover-templates/${form.id}`, {
          name: form.name.trim(),
          message: form.message.trim(),
          is_default: form.is_default,
        });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/fax-cover-templates`, {
          name: form.name.trim(),
          message: form.message.trim(),
          is_default: form.is_default,
        });
        toast.success("Template created");
      }
      setDialogOpen(false);
      setForm(EMPTY);
      fetchTemplates();
    } catch (err) {
      console.error("Save failed", err);
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/fax-cover-templates/${id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete template");
    }
  };

  return (
    <div className="space-y-6" data-testid="fax-templates-settings">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Fax Cover Message Templates
            </CardTitle>
            <CardDescription>
              Save reusable cover-page messages (&ldquo;Please sign and return&rdquo;, &ldquo;For your records&rdquo;, etc.). Staff can pick from these templates in the Send Fax dialog.
            </CardDescription>
          </div>
          <Button
            onClick={() => openCreate()}
            className="gap-2 bg-teal-600 hover:bg-teal-700"
            data-testid="new-fax-template-btn"
          >
            <Plus className="w-4 h-4" />
            New Template
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading templates…
            </div>
          ) : templates.length === 0 ? (
            <div className="space-y-4">
              <div className="text-center py-8 rounded-md border border-dashed border-slate-200 bg-slate-50">
                <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-600 font-medium">No templates yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  Create your first template or start from a suggestion below.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500 tracking-wide">
                  Quick-start suggestions
                </Label>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => openCreate(s)}
                      className="text-left rounded-md border border-slate-200 bg-white hover:bg-teal-50 hover:border-teal-300 p-3 transition"
                      data-testid={`suggested-template-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div className="text-sm font-medium text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {s.message}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="flex items-start justify-between gap-4 rounded-md border border-slate-200 bg-white p-3 hover:border-teal-300 transition"
                  data-testid={`template-row-${tpl.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900 truncate">{tpl.name}</h4>
                      {tpl.is_default && (
                        <Badge className="bg-amber-100 text-amber-800 gap-1">
                          <Star className="w-3 h-3 fill-current" /> Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                      {tpl.message}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(tpl)}
                      data-testid={`edit-template-${tpl.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-template-${tpl.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this template?</AlertDialogTitle>
                          <AlertDialogDescription>
                            &ldquo;{tpl.name}&rdquo; will be removed. Staff will no longer see it in the Send Fax dialog. This can&apos;t be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(tpl.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {form.id ? "Edit Template" : "New Cover Message Template"}
            </DialogTitle>
            <DialogDescription>
              Give it a short name and write the message that staff can drop into any fax cover page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="tpl-name">Template Name</Label>
              <Input
                id="tpl-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Please sign and return"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tpl-msg">Message</Label>
              <Textarea
                id="tpl-msg"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="Please review, sign, and fax back at your earliest convenience."
                rows={4}
                data-testid="template-message-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div>
                <div className="text-sm font-medium">Set as default</div>
                <div className="text-xs text-slate-500">
                  Automatically pre-fills into the cover page when opening a new fax.
                </div>
              </div>
              <Switch
                checked={form.is_default}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_default: v }))}
                data-testid="template-default-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-template-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {form.id ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
